/**
 * FolderTableView · AI 分类页文件夹列表（表格）视图（react-virtualized List 虚拟化版）
 *
 * 结构：FolderListHeader 固定在顶部（不进滚动区），下方 AutoSizer + List 内部滚动，
 * 只渲染可见行（+ overscan）。复用现有 FolderRow（多选 / 右键 / hover 操作 / 排序均不变）。
 * 待归档虚拟项作为最后一行，不挂 Dropdown。
 *
 * 表格视图不支持框选 / 拖动，选中态只随点击 / 全选变化（都发生在非滚动时），
 * 故无需 grid 那套"滚动中清缓存"处理：rowRenderer 闭包每次捕获最新 selectedFolderIds，
 * List 据此 shallowCompare 重渲染即可。
 */

import React from 'react'
import { AutoSizer, List, ListRowProps } from 'react-virtualized'
import { Dropdown } from 'antd'
import FolderRow from './FolderRow'
import FolderListHeader, { FolderListSort } from './FolderListHeader'
import { TABLE_ROW_HEIGHT } from './gridLayout'
import styles from './FolderList.module.scss'
import type { FolderListItem } from '../../../../types/ai-classify'

interface FolderTableViewProps {
  /** 当前已排序的顶层文件夹列表（来自 sortedTopFolders） */
  folders: FolderListItem[]
  /** 「待归档」虚拟项，非空时追加在列表末尾；不参与多选 */
  unclassifiedFolder: FolderListItem | null
  /** 当前列表排序状态（来自 Redux uiSlice） */
  listSort: FolderListSort
  /** 切换排序（dispatch setFolderSort） */
  setListSort: (next: FolderListSort) => void
  /** 构造单个文件夹的右键菜单（批量 / 单条由 FolderList 内部判断） */
  buildFolderContextMenu: (folder: FolderListItem) => React.ReactElement
  /** 多选模式开关 */
  manageMode: boolean
  /** 当前已勾选的 folderId 列表 */
  selectedFolderIds: string[]
  /** 正在归档中的 folderId 集合 */
  archivingFolderIds: Set<string>
  /** 归档已超时的 folderId 集合 */
  timeoutFolderIds: Set<string>
  /** 是否管理员（控制 canManage） */
  isAdmin: boolean
  /** 行整块点击（跳详情 / 切换勾选） */
  onRowClick: (folderId: string) => void
  /** checkbox 单独点击（进多选 + toggle） */
  onSelectClick: (folderId: string) => void
  /** 单条下载（hover 操作按钮） */
  singleDownload: (folder: FolderListItem) => void
  /** 单条重命名（右键菜单 / hover 操作按钮，打开重命名 Modal） */
  handleSingleRenameStart: (folder: FolderListItem) => void
  /** 单条删除（右键菜单 / hover 操作按钮，弹 Modal.confirm） */
  handleSingleDelete: (folder: FolderListItem) => void
}

/**
 * 文件夹列表（表格）视图（虚拟化）
 *
 * 用 react-virtualized AutoSizer + List 替换原 .listView 全量 map 渲染。
 * · AutoSizer 撑满 .tableListWrap 并把宽高注入 List
 * · 待归档虚拟项追加在 cells 末尾
 */
const FolderTableView: React.FC<FolderTableViewProps> = ({
  folders,
  unclassifiedFolder,
  listSort,
  setListSort,
  buildFolderContextMenu,
  manageMode,
  selectedFolderIds,
  archivingFolderIds,
  timeoutFolderIds,
  isAdmin,
  onRowClick,
  onSelectClick,
  singleDownload,
  handleSingleRenameStart,
  handleSingleDelete
}) => {
  // 待归档虚拟项追加到列表末尾，作为最后一行
  const cells: FolderListItem[] = unclassifiedFolder
    ? [...folders, unclassifiedFolder]
    : folders

  const rowRenderer = ({
    index,
    key,
    style
  }: ListRowProps): React.ReactNode => {
    const folder = cells[index]

    // 待归档虚拟项：不挂 Dropdown、不传选中 props（银灰 + 禁选由 FolderRow 据 folder.virtual 自处理）
    if (folder.virtual) {
      return (
        <div key={key} style={style}>
          <FolderRow folder={folder} onClick={onRowClick} />
        </div>
      )
    }

    return (
      <div key={key} style={style}>
        <Dropdown
          popupRender={() => buildFolderContextMenu(folder)}
          trigger={['contextMenu']}
          disabled={manageMode}
        >
          <FolderRow
            folder={folder}
            onClick={onRowClick}
            onSelectClick={onSelectClick}
            archiving={archivingFolderIds.has(folder.folderId)}
            archivingTimedOut={timeoutFolderIds.has(folder.folderId)}
            selectMode={manageMode}
            selected={
              manageMode && selectedFolderIds.indexOf(folder.folderId) >= 0
            }
            onDownload={singleDownload}
            onRename={handleSingleRenameStart}
            onDelete={handleSingleDelete}
            canManage={isAdmin}
          />
        </Dropdown>
      </div>
    )
  }

  return (
    <div className={styles.tableView}>
      {/* 表头固定在顶部 · 与行同为通栏布局、列宽一致，自然对齐（行的 16px 右内边距已让开滚动条） */}
      <FolderListHeader sort={listSort} onSortChange={setListSort} />
      <div className={styles.tableListWrap}>
        <AutoSizer>
          {({ width, height }) => (
            <List
              width={width}
              height={height}
              rowCount={cells.length}
              rowHeight={TABLE_ROW_HEIGHT}
              rowRenderer={rowRenderer}
              overscanRowCount={6}
              style={{ outline: 'none' }}
            />
          )}
        </AutoSizer>
      </div>
    </div>
  )
}

export default FolderTableView
